import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import NavigationHeader from '../../components/navigation/NavigationHeader';
import { Button } from '@ab-inbev-maz/compass';
import { FifthStep, FourthStep, ThirdStep, SecondStep, FirstStep, TycStep, LayoutStep } from '@/components/craft/CreateCraft';
import { partialCraftSchema, type CraftSchema } from '@/schemas/craft/create';
import AccordionGroup, { type AccordionGroupRef } from '@/components/commos/AccordionGroup';
import type z from 'zod';
import { useCraftStore } from '@/stores/craftStore';
import Loading from '@/features/sso-auth/components/Loading';
import { CraftContext } from '@/contexts/craftContext';
import { useAlertStore } from '@/stores/alertStore';
import { useNavigate } from 'react-router-dom';
import ButtonDownloadTemplate from '@/components/ButtonDownloadTemplate';

const emptyIconStyle = {
    '--button-empty-icon-width': '0'
};

const accordionList = [
    {
        id: 'action',
        title: '¿Qué vamos a hacer hoy?',
        Component: FirstStep,
        enabledTo: ['manual_creation', 'template_based']
    },
    {
        id: 'formats',
        title: '¿Qué formato utilizaremos?',
        Component: SecondStep,
        enabledTo: ['manual_creation']
    },
    {
        id: 'taxonomies',
        title: '¿Qué taxonomía usaremos?',
        Component: ThirdStep,
        enabledTo: ['manual_creation']
    },
    {
        id: 'layout',
        title: 'Selección de taxonomía',
        Component: LayoutStep,
        enabledTo: ['template_based']
    },
    {
        id: 'templates',
        title: 'Selecciona tu template',
        Component: FourthStep,
        enabledTo: ['manual_creation', 'template_based']
    },
    {
        id: 'tyc',
        title: 'Legales',
        Component: TycStep,
        enabledTo: ['template_based']
    },
    {
        id: 'parameters',
        title: 'Carga masiva de parámetros',
        Component: FifthStep,
        enabledTo: ['manual_creation', 'template_based']
    },
]

const shouldShowLegalStep = (taxonomyId: string) => {
    const layout = [
        { id: 'default', showLegal: false },
        { id: 'default_top', showLegal: false },
        { id: 'lineal', showLegal: false },
        { id: 'overlap', showLegal: false },
        { id: 'grid', showLegal: false },
    ].find(({ id }) => id === taxonomyId);

    return layout ? layout.showLegal : true;
};

const CraftPage: React.FC = () => {
    const ref = useRef<AccordionGroupRef | null>(null);
    const [formData, updateFormData] = useReducer((prev, next) => {
        return next.elementType
            ? { elementType: next.elementType, file: null }
            : { ...prev, ...next };
    },
        { elementType: '', file: null, formatType: '', taxonomy: '', image_rule_id: '', template_id: '', parameters: '', legal: '' }
    );
    const navigate = useNavigate();
    const showAlert = useAlertStore(state => state.showAlert);

    const save = useCraftStore(state => state.saveForm);
    const craftData = useCraftStore(state => state.data);
    const populate = useCraftStore(state => state.populateForm);
    const getTaxonomies = useCraftStore(state => state.getTaxonomies);
    const getTemplates = useCraftStore(state => state.getTemplates);
    const getLegals = useCraftStore(state => state.getLegals);

    const resetFormatType = useCraftStore(state => state.resetFormatType);

    const [isFetching, setIsFetching] = useState(false);
    const [localTemplateName, setLocalTemplateName] = useState('');

    // DATA FORMATTING
    const getAction = useCallback((actionId: string) => {
        return craftData?.actions?.find(({ id }) => id === actionId)?.name;
    }, [craftData]);

    const getFormat = useCallback((formatId: string) => {
        return craftData?.formats?.find(({ id }) => id === formatId)?.name;
    }, [craftData]);

    const getTaxonomy = useCallback((taxonomyId: string) => {
        return craftData?.taxonomies?.find(({ id }) => id === taxonomyId)?.name;
    }, [craftData]);

    const getTemplate = useCallback((templateId: string) => {
        const templateName = craftData?.templates?.find(({ id }) => id === templateId)?.name;
        if (templateName) setLocalTemplateName(templateName);
        return craftData?.templates?.find(({ id }) => id === templateId)?.name || localTemplateName;
    }, [craftData]);

    const TAXONOMY_NAMES: Record<string, string> = {
        default: 'Vertical',
        default_top: 'Vertical - Espacio Superior (Peru)',
        lineal: 'Lineal',
        overlap: 'Sobrepuesto',
        grid: 'Cuadrícula',
    };

    const getTaxonomyName = useCallback((taxonomyId: string) => {
        return TAXONOMY_NAMES[taxonomyId] || taxonomyId;
    }, []);

    // ACCORDION ITEMS
    const accordionActivated = useMemo(() => {
        if (!formData.elementType) {
            return accordionList.slice(0, 1).map(({ enabledTo, ...item }) => item);
        }
        return accordionList
            .filter(({ enabledTo, id }) =>
                enabledTo?.includes(formData.elementType)
                && (id !== 'tyc' || shouldShowLegalStep(formData.taxonomy)))
            .map(({ enabledTo, id, ...item }) => {
                    switch (id) {
                        case 'action':
                            return { ...item, description: getAction(formData.elementType) };
                        case 'formats':
                            return { ...item, description: getFormat(formData.formatType) };
                        case 'taxonomies':
                            return { ...item, description: getTaxonomy(formData.image_rule_id) };
                        case 'layout':
                            return { ...item, description: getTaxonomyName(formData.taxonomy) };
                        case 'tyc':
                            return { ...item, description: formData.legal ? 'Legal seleccionado' : '' };
                        case 'templates':
                            return { ...item, description: getTemplate(formData.template_id) };
                        case 'parameters':
                            return { ...item, description: formData.parameters };
                        default:
                            return item;
                    }
                }
                );
    }, [formData, getAction, getFormat, getTaxonomy, getTaxonomyName, getTemplate]);

    // ON STEP COMPLETE
    const onFirstStepComplete = useCallback(async (data: z.infer<typeof partialCraftSchema>) => {
        updateFormData(data);
        if (data.elementType === 'template_based') {
            await resetFormatType();
            await getTemplates({ formatType: '1_1' });
            await getLegals();
        }
        ref.current?.next();
    }, []);

    const onSecondStepComplete = useCallback(async (data: z.infer<typeof partialCraftSchema>) => {
        updateFormData(data);
        if (data.formatType) {
            await getTaxonomies(data.formatType);
            await getTemplates({ formatType: data.formatType });
        }
        ref.current?.next();
    }, [getTaxonomies, getTemplates]);

    const onLayoutStepComplete = useCallback((data: z.infer<typeof partialCraftSchema>) => {
        updateFormData(data);
        ref.current?.next();
    }, []);

    const onThirdStepComplete = useCallback((data: z.infer<typeof partialCraftSchema>) => {
        updateFormData(data);
        ref.current?.next();
    }, []);

    const onFourthStepComplete = useCallback((data: z.infer<typeof partialCraftSchema>) => {
        updateFormData(data);
        ref.current?.next();
    }, []);

    const onTycStepComplete = useCallback((data: z.infer<typeof partialCraftSchema>) => {
        updateFormData(data);
        ref.current?.next();
    }, []);

    const onFifthStepComplete = useCallback(async (file: File | null) => {
        updateFormData({ file });
    }, []);


    // ON SUBMIT
    const onSubmit = useCallback(async () => {
        const data = formData as unknown as CraftSchema;

        setIsFetching(true);
        const result = await save(data);
        if (result.success) {
            showAlert({
                text: 'Manualidad guardada exitosamente',
                message: `La manualidad "${data.file?.name}" ha sido guardada`,
                status: 'success',
            });
            // navigate(`/combos/table/${result.process_image_id}/${result.isCombo ? 'bus' : 'manual'}`);
            void navigate(`/combos/table/${result.process_image_id}/bus`);
        }
        else {
            showAlert({
                text: 'Error al guardar manualidad',
                message: result.message,
                status: 'error',
            });
        }
        setIsFetching(false);
    }, [formData]);

    // CONTEXT VALUE
    const contextValue = useMemo(() => ({
        onFirstStepComplete,
        onSecondStepComplete,
        onLayoutStepComplete,
        onThirdStepComplete,
        onFourthStepComplete,
        onTycStepComplete,
        onFifthStepComplete,
        isFetching
    }), [onFirstStepComplete, onSecondStepComplete, onLayoutStepComplete, onThirdStepComplete, onFourthStepComplete, onFifthStepComplete, onTycStepComplete, isFetching]);

    useEffect(() => {
        void populate();
    }, []);

    if (isFetching) {
        return (
            <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-sm w-full space-y-8 bg-white p-8 rounded-3xl shadow-lg">
                    <Loading message="Esto solo va tomar un momento, estamos generando el proceso de carga." />
                </div>
            </div>
        );
    }

    if (!craftData) {
        return (
            <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-sm w-full space-y-8 bg-white p-8 rounded-3xl shadow-lg">
                    <Loading message="Esto solo va tomar un momento, estamos cargando los errores." />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100" style={emptyIconStyle as React.CSSProperties}>
            <NavigationHeader
                title="Creación de imágenes manuales"
                description="Crea y personaliza tus combos o banners a tu medida de forma fácil y rápida."
                backLink="/"
                actions={[
                    <ButtonDownloadTemplate
                        fileName="TemplateCraftManual.xlsx"
                        label="Template otros formatos"
                    />,
                    <ButtonDownloadTemplate
                        fileName="TemplateCraftCombo.xlsx"
                        label="Template combos"
                    />,
                    <Button
                        label="Guardar nueva imagen"
                        rightIcon="media/image-add"
                        variant="primary"
                        disabled={!formData.file || isFetching}
                        className="cursor-pointer"
                        onClick={onSubmit}
                    />
                ]}
            />
            <div className="container mx-auto px-4 py-8">
                <div className="col-span-4 flex flex-col gap-4">
                    <CraftContext value={contextValue}>
                        <AccordionGroup items={accordionActivated} ref={ref} />
                    </CraftContext>
                </div>
            </div>
        </div>
    );
};

export default CraftPage;
